import { useState } from 'react';
import { toast } from 'react-toastify';
import { bulkAddKeyPickupFromAPI } from 'src/services/client';
import {
  IBulkKeyPickupResponse,
  IBulkKeyPickupResponseUnit,
} from 'src/services/client-types';

export interface KeyValuePair {
  id: string;
  key: string;
  value: string;
}

export interface KeyValPairNoID {
  key: string;
  value: string;
}

export interface KeyValPairWithOrder {
  key: string;
  value: string;
  order: number;
}

export interface BulkKeyPickupPayloadFromUI {
  SuiteIds: string[];
  KeyHandOffInstructions: string;
  SuiteKeysAndValues: KeyValPairNoID[];
  propertyId: number;
}

export interface BulkKeyPickupPayloadToAPI {
  SuiteIds: string[];
  KeyHandOffInstructions: string;
  SuiteKeysAndValues: KeyValPairWithOrder[];
  propertyId: number;
}

export interface OrderedBulkKeyPickupPayload {
  SuiteIds: string[];
  KeyHandOffInstructions: string;
  SuiteKeysAndValues: KeyValPairWithOrder[];
  propertyId: number;
}

export const applyOderToSuiteKeysValues = (
  payload: BulkKeyPickupPayloadFromUI
): OrderedBulkKeyPickupPayload => {
  const suitesKeysAndValuesOrdered: KeyValPairWithOrder[] =
    payload.SuiteKeysAndValues.map(
      (suiteEntry: KeyValPairNoID, index: number) => {
        return {
          ...suiteEntry,
          order: index + 1,
        };
      }
    );

  return {
    SuiteIds: payload.SuiteIds,
    KeyHandOffInstructions: payload.KeyHandOffInstructions,
    SuiteKeysAndValues: suitesKeysAndValuesOrdered,
    propertyId: payload.propertyId,
  };
};

export interface UseBulkKeyPickupsReturn {
  bulkKeyPickupsCreated: IBulkKeyPickupResponseUnit[];
  isLoading: boolean;
  error: string | null;
  saveBulkKeyPickups: (payload: any) => Promise<void>;
  clearBulkKeyPickupsState: () => void;
}

export const useBulkKeyPickups = (): UseBulkKeyPickupsReturn => {
  const [bulkKeyPickupsCreated, setBulkKeyPickupsCreated] = useState<
    IBulkKeyPickupResponseUnit[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveBulkKeyPickups = async (payload: BulkKeyPickupPayloadFromUI) => {
    try {
      setIsLoading(true);
      setError(null);

      const transformedPayload: BulkKeyPickupPayloadToAPI =
        applyOderToSuiteKeysValues(payload);

      const response: IBulkKeyPickupResponse =
        await bulkAddKeyPickupFromAPI(transformedPayload);

      setBulkKeyPickupsCreated(response.data || []);
    } catch (err: any) {
      console.error('Failed to create bulk key pickups:', err);
      const errorMessage =
        err.message ||
        'Failed to create the following bulk key pickups. Please try again.';
      setError(errorMessage);

      toast.error(errorMessage, {
        position: 'top-center',
        autoClose: 7000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearBulkKeyPickupsState = () => {
    setBulkKeyPickupsCreated([]);
    setError(null);
  };

  return {
    bulkKeyPickupsCreated,
    isLoading,
    error,
    saveBulkKeyPickups,
    clearBulkKeyPickupsState,
  };
};
